import getI18nProps from '@/utils/i18/getI18nProps';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import { Trans, useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';

import bannerBg from './assets/banner_bg.webp';
import stage1 from './assets/stage1.png';
import stage2 from './assets/stage2.png';
import stage3 from './assets/stage3.png';
import stage4 from './assets/stage4.png';
import stage10min from './assets/stage-10min.png';
import stageTg from './assets/stage-tg.png';
import arrow from './assets/stage_arrow.svg';
import depositAll from '../../../components/biz/rewards/assets/depositAll.png';

import Image from 'next/image';
import WrappedButton from '@/components/ui/WrappedButton';
import { useQuery } from '@tanstack/react-query';
import { queryDailyRewardsList } from '@/apis/rewards';
import queryKeysFactory from '@/reactQueryKeys/keysFactory';
import { UnpackPromise } from '@/utils/type';
import { relative } from 'path';
import useLoginModal from '@/hooks/biz/useLoginModal';
import { globalModals, GLOBAL_MODALS } from '@/state/globalSettings';
import { useSetAtom } from 'jotai';
import GameWagerTable from '@/components/biz/rewards/gameWagerTable';
import { ArrowDropDown } from '@mui/icons-material';
import useTheme from '@/theme/useTheme';
import Link from 'next/link';

const BONUS_COUNT = 4;

// export const runtime = 'experimental-edge';

const DepositBenefits: React.FC = () => {
  const [t] = useTranslation(['benefits']);
  const { data, isLoading } = useQuery<
    UnpackPromise<ReturnType<typeof queryDailyRewardsList>>
  >(queryKeysFactory.rewards.dailyRewardsList());

  const [isLogin, openLoginModal] = useLoginModal();
  const setModals = useSetAtom(globalModals);

  const { theme } = useTheme();

  const depositBonusInfo = useMemo(() => {
    const rewardsData = data?.data?.rewards.find(
      (r) => r.bonus === 'DEPOSITBONUS',
    );
    return rewardsData;
  }, [data]);

  const completedCount =
    Math.min(depositBonusInfo?.completedCount, BONUS_COUNT - 1) || 0;

  const auroraProps = {
    content: '""',
    display: 'block',
    position: 'absolute',
    right: 0,
    bottom: '-100px',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    zIndex: -1,
    backgroundImage: 'radial-gradient(circle, #fff 0%, #FECF4A 100%)',
    filter: 'blur(30px)',
  };

  const depositBtn = (index) => (
    <WrappedButton
      sx={{
        alignSelf: 'center',
        filter:
          index === completedCount ? undefined : 'blur(12px) brightness(0.2)',
      }}
      onClick={() => {
        if (!isLogin) {
          openLoginModal();
        } else {
          setModals({
            [GLOBAL_MODALS.WALLET]: true,
          });
        }
      }}
    >
      {t('Deposit Now')}
    </WrappedButton>
  );

  const tcBlock = (type?) => {
    const isReload = type === 'reload';
    return (
      <Stack
        mt={4}
        gap={0}
        sx={{
          lineHeight: 1.8,
        }}
        color={'text.secondary'}
      >
        <Typography
          variant="h4"
          color={'text.primary'}
          component={'a'}
          id={isReload ? 'reload-bonus' : 'welcome-bonus'}
        >
          {t('benefits::benefits_deposit_4', {
            defaultValue: isReload
              ? 'Thursday Reloaded Deposit Bonus Terms and Conditions'
              : 'Welcome Bonus Terms and Conditions',
          })}
        </Typography>
        <ol
          style={{
            paddingInlineStart: '1em',
          }}
        >
          <li>
            {isReload
              ? t('benefits::reload_deposit_1', {
                  defaultValue:
                    'To claim Thursday Reload bonus, the deposit must be made on Thursday ( 00:00 UTC - 23:59 UTC ). The qualifying deposit must be made as a single transaction. The bonus is available only on Thursday, once a week.',
                })
              : t('benefits::benefits_deposit_8', {
                  defaultValue:
                    'You need to select this bonus in the Wallet > Deposit and make a successful deposit to activate it.',
                })}
          </li>
          <li>
            {isReload
              ? t('benefits::reload_deposit_2', {
                  defaultValue:
                    'Bonus is available to the players who have made at least 2 deposits.',
                })
              : t('benefits::benefits_deposit_9', {
                  defaultValue:
                    'Welcome Bonus offers will only be available for 30 days upon successful registrations.',
                })}
          </li>
          <li>
            {isReload ? (
              <Trans
                i18nKey="benefits::reload_deposit_3"
                components={{
                  tcLink: (
                    <Link
                      href="/rewards"
                      style={{
                        color: theme.palette.primary.main,
                        textDecoration: 'underline',
                      }}
                    />
                  ),
                }}
                defaults="Bonus will not be available if you have Welcome Bonus activated in the <tcLink>Reward Dashboard.</tcLink>"
              ></Trans>
            ) : (
              t('benefits::benefits_deposit_10', {
                defaultValue:
                  'The minimum deposit required to activate this bonus is $20 USDT.',
              })
            )}
          </li>
          {isReload ? (
            <>
              <li>
                {t('benefits::reload_deposit_4', {
                  defaultValue:
                    'You need to select this bonus in the Wallet > Deposit and make a successful deposit to activate it.',
                })}
              </li>
              <li>
                {t('benefits::reload_deposit_5', {
                  defaultValue:
                    'The minimum deposit required to activate this bonus is $20 USDT.',
                })}
              </li>
            </>
          ) : null}
          <li>
            {isReload
              ? t('benefits::reload_deposit_6', {
                  defaultValue:
                    'The deposit bonus 50% up to $200 USDT or currency equivalent.',
                })
              : t('benefits::benefits_deposit_11', {
                  defaultValue:
                    'The maximum first Welcome Bonus is 200% up to $2,000 USDT or currency equivalent, but you need to deposit within 60 minutes of signing up for a new account to get the full 200%. Any deposit made beyond 60 minutes after signing up for a new account will get the maximum first Welcome Bonus of 125% up to $2,000 USDT.',
                })}
          </li>
          <li>
            {isReload
              ? t('benefits::reload_deposit_7', {
                  defaultValue:
                    'The total wagering requirement for each bonus is 50x.',
                })
              : t('benefits::benefits_deposit_12', {
                  defaultValue:
                    'The total wagering requirement for each bonus is 60x.',
                })}
          </li>
          <li>
            {isReload
              ? t('benefits::reload_deposit_8', {
                  defaultValue:
                    'You have 6 days to fulfill the bonus wagering requirements. If the wagering requirements are not met during this time, the bonus money and any bonus winnings will be void.',
                })
              : t('benefits::benefits_deposit_13', {
                  defaultValue:
                    'You have 14 days to fulfill the bonus wagering requirements, and this period includes the 30 days to claim the bonus. If the wagering requirements are not met during this time, the bonus money and any bonus winnings will be void.',
                })}
          </li>
          <li>
            {t('benefits::benefits_deposit_14', {
              defaultValue:
                'The wagering contribution varies across casino games:',
            })}
            <br />
            <GameWagerTable depositBonusInfo={depositBonusInfo} />
          </li>
          <li>
            {t('benefits::benefits_deposit_15', {
              defaultValue:
                'Not all game types contribute the same to the wagering requirements (Listed below).',
            })}
          </li>
          <li>
            {isReload
              ? t('benefits::reload_deposit_9', {
                  defaultValue:
                    'Only one Deposit Bonus offer can be active at a time.',
                })
              : t('benefits::benefits_deposit_16', {
                  defaultValue:
                    'Only one Welcome Bonus offer can be active at a time.',
                })}
          </li>
          <li>
            {t('benefits::benefits_deposit_17', {
              defaultValue:
                'Only one bonus is awarded per person (e-mail/browser/device/IP address), unless stated otherwise.',
            })}
          </li>
          {isReload ? null : (
            <li>
              {t('benefits::benefits_deposit_18', {
                defaultValue:
                  'Only once the wagering requirements are met on the first Welcome Bonus, can the subsequent welcome bonuses be claimed:',
              })}
              <br />
              <ol
                style={{
                  listStyle: 'lower-alpha',
                  paddingInlineStart: '1em',
                }}
              >
                <li>
                  {t('benefits::benefits_deposit_18_1', {
                    defaultValue:
                      'The second Welcome Bonus earns you 150%, up to $2,000 USDT or currency equivalent.',
                  })}
                </li>
                <li>
                  {t('benefits::benefits_deposit_18_2', {
                    defaultValue:
                      'The third Welcome Bonus earns you 175%, up to $2,000 USDT or currency equivalent.',
                  })}
                </li>
                <li>
                  {t('benefits::benefits_deposit_18_3', {
                    defaultValue:
                      'The fourth Welcome Bonus earns you 200%, up to $2,000 USDT or currency equivalent.',
                  })}
                </li>
              </ol>
            </li>
          )}
          <li>
            {t('benefits::benefits_deposit_19', {
              defaultValue:
                'We reserve the right to amend, suspend or cancel the promotion at any time without prior notice.',
            })}
          </li>
          <li>
            {t('benefits::benefits_deposit_20', {
              defaultValue:
                'We reserve the right to exclude any player from participating in this promotion if any of the mentioned conditions are not met.',
            })}
          </li>
          <li>
            {t('benefits::benefits_deposit_21', {
              defaultValue:
                "These Terms and Conditions are subject to the site's General Terms and Conditions and the General Bonus Terms and Conditions. In the event of any conflict between these Terms and the General Terms, these Terms shall prevail.",
            })}
          </li>
        </ol>
      </Stack>
    );
  };

  const gamesBlock = (
    <Accordion
      sx={{
        border: (theme) => theme.palette.border.normal,
        borderRadius: 1,
      }}
    >
      <AccordionSummary
        expandIcon={<ArrowDropDown />}
        aria-controls="panel1-content"
        id="panel1-header"
        sx={{
          padding: 2,
        }}
      >
        <Typography>{t('benefits::Excluded Games')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="caption">
          All Red Tiger games; All Livespins games; Dead or Alive 2, Vampires,
          The True Sheriff, The Ninja, Avalon, Castle Builder, Castle Builder 2,
          Good to go, Hellboy, Immortal Romance, Joker 8000, Lucky Little Gods,
          Drive: Multiplier Mayhem, Jack and the Beanstalk, Jack Hammer, Jack
          Hammer 2: Fishy Business, Joker Pro, Koi Princess, Mega Joker, Reel
          Rush, Secrets of Atlantis, Starburst, Steam Tower, Victorious, Aztec
          Idols, Book of Dead, GEMiX, Leprechaun Goes to Hell, Moon Princess,
          Multifruit 81, Pearls of India, Reactoonz, Royal Masquerade, Tower
          Quest, Viking Runecraft,Power Force Villains, Treasure Island, Laser
          Fruit, Lucky Little Devil, Mystery Reels, Wild Circus, Voodoo Gold,
          Cygnus, Adventure Palace, Dr. Jedkyll & Mr. Hyde, Devil&apos;s
          Delight, Dragon Ship, Golden Legend, Guns N&apos; Roses, Happy
          Halloween, Holiday Season, Hugo 2, Medusa, Pimped, Pinocchio, Riches
          of RA, Sea Hunter, SpectacularWheel of Wealth, The Wish Master,
          Thunderstruck, Untamed Bengal Tiger, Untamed Wolf Pack, Vikings Go
          Berzerk, Wheel of Wealth, Wheel of Wealth Special Edition, Extra
          Chilli, Bonanza, Ecuador Gold, Legacy of Dead, Mongol
          Treasure,Minotaurus, Voodoo, Voodoo Dice, Rise of Merlin, Flame
          Busters, Fruit Warp, Pink Elephants, Rocket Fellas Inc, The Falcon
          Huntress, Midas Golden Touch, Dragon Horn, Ravens Eye, Riders of the
          Storm, Divine Lotus, Sword of Khans, Esqueleto Explosivo 2, Dawn of
          Egypt, 11 Champions, 3 Tiny Gods, 300 Shields, Africa X up, African
          Quest, Age of Conquest, Agent Valkyrie, Alchemy Blast, Amazing Link
          Apollo, Amber Sterling&apos;s Mystic Shrine, Art of the Heist,
          Atlantis Rising, Augustus, Avalon 2, Banana Drop, Beautiful Bones,
          Bikini Party, Book of Oz, Bookie of Odds, Break Away Lucky Wilds,
          Break da Bank Again Respin, Burlesque by Dita, Castle Builder II,
          Chronicles of Olympus X Up, Cool Buck 5 Reel, Diamond King Jackpots,
          Dragon Dance, Dragon&apos;s Myth, Dragonz, Elite of Evil, Finer Reels
          of Life, Flower Fortunes, Forsaken Kingdom, Gems Odyssey, Gems Odyssey
          92, God of Fire, Gods of Power, Goldaur Guardians, Golden Stallion,
          Hot Ink, Immortal Romance Mega Moolah, Jurassic Park, Jurassic World,
          Le Kaffee Bar, Lucky Clucks, Magic of Sahara, Monster Blast, Mustang
          Riches, Peek-a-Boo, Pets go Wild, Pirates Quest, Portal of Gold,
          Rabbit in the Hat, Reel Gems, Retro Reels, Retro Reels Diamond Glitz,
          Retro Reels Extreme Heat, Santa Goes Wild, Scarab Kingdom, Scrooge,
          Sisters of Oz WOWPOT, Spectacular Wheel of Wealth, Squelin&apos;
          Riches, Stardust, Sweet Chilli, Thunderstruck II, Thunderstruck Wild
          Lightning, Tomb Raider, Tomb Raider Secret of the Sword, Untamed
          Crowned Eagle, Untamed Giant Panda, Vampire: The Masquerade - Las
          Vegas, Village People, Wild Catch, Wild Orient, Zombie Hoard, Lucky
          Angler, Reel Rush 2, Reel Steal, Robin Hood: Shifting Riches,
          Scudamore&apos;s Super Stakes, Wilderland, Steam Vault, Queens of
          Glory, Tiki Terror, Wacky Wildlife, Golden Stripe, Flexing Dragons,
          Solar King, Solar Queen, Solar Queen Megaways, Solar Temple, Juice
          Inc, The Champions, Bronco Spirit, Golden Beauty, Queen of Gold,
          Jungle Gorilla, 3 Kingdoms Battle of Red Cliffs, Cash Elevator, Dragon
          Kingdom, Jade Butterfly, Jammin Jars 2, Wild Swarm, The Shadow Order,
          10001Nights, 5 Families, Agent Royale, Ancients Blessing, Arcade Bomb,
          Atlantis, Aurum Codex, Aztec Spins, Betty Boris And Boo, Bombuster,
          Cash Ultimate, Diamond Blitz, Dragons Fire, Dragons Fire Infini Reels,
          Dragons Fire MegaWays, Dynamite Riches, Eagle Riches, Fruit Snap, Gems
          Gone Wild Power Reels, Gods Of Troy, Golden Lotus, Golden Offer,
          Golden Tsar, Gonzos Quest MegaWays, Harle Coin, Hoard Of Poseidon,
          Jewel Scarabs, Jingle Bells Power Reels, Lion Dance, Lucky Fridays,
          Lucky Oktoberfest, Mega Pyramid, MegaRise, Multiplier Riches, Mystery
          Reels MegaWays, Mystic Staxx, Night Roller, Piggy Pirates, Pirates
          Plenty, Pirates Plenty Battle For Gold, Pirates Plenty MegaWays,
          Primate King, RAs Legend, Reel Heist, Reel Keeper, Reel King Mega,
          Reptizillions Power Reels, Riddle Of The Sphinx, Rio Stars, Royal
          Gems, Spin Town, Sumo Spins, Sylvan Spirits, The Wisecracker
          Lightning, Three Musketeers, Trillionaire, Well of Wishes, Well Of
          Wilds MegaWays, Wild Circus, Wild Elements, Wild Expedition, Wild Hot
          Chilli Reels, Wild O&apos;Clock, Wild Spartans, Win Escalator, Zeus
          Lightning Power Reels, 1 Reel Egypt, 1 Reel Fruits, 1 Reel Halloween,
          1 Reel Monkey, 1 Reel Xmas, 88 Lucky Fortunes, Aztec Spell, Aztec
          Spell 10 Lines Edition, Beer Collection 20 Lines, Beer Collection 30
          Lines, Book of Demi Gods III, Book of Rebirth, Book of the Divine,
          Colossus Fruits, Cupids&apos; Strike 2, Demi Gods III, Demi Gods III -
          15 Lines, Demi Gods III 15 Lines, Divine Forest, Egyptian Eclipse,
          Egyptian Rebirth 2, Egyptian Rebirth II 10 Lines, Fruits Collection 10
          Lines, Fruits Deluxe, Fruits Deluxe Christmas Edition, Fruits Deluxe
          Easter Edition, Gangsters Gold, Goddess Of Lotus, Goddess Of Lotus 10
          Lines, Lemur Does Vegas, Lemur Does Vegas Easter Edition, Lilith
          Passion 15 Lines Edition, Lilith&apos;s Passion, Lilith&apos;s Passion
          Christmas Edition, Magical Amazon, Mines Of Gold, Penny Fruits Xtreme,
          Queen Of Fire, Sirens Treasures, Sirens Treasures 15 Lines Edition,
          Spinning Beers, Spinoween, Stake&apos;s Xtreme Fruits, Story of The
          Samurai, Story Of The Samurai 10 Lines, Summer Splash, 9 Lions,
          American Poker V, Arcade, Black Horse, Black Horse Deluxe, Butterfly
          Lovers, Fruit Mania, Fruit Mania Deluxe, Hot 777, Hot 777 Deluxe,
          KickOff, Larry the Leprechaun, Magic Poker, Neon City, Power of Gods
          Egypt, Power of Gods Hades Hold the Jackpot, Power of Gods Medusa,
          Power of Gods The Pantheon, Reel Hero, Relic Hunters and the Book of
          Faith, Sizzling Bells, Sonic Reels, Telly Reels, Vegas Reels II, Wolf
          Gold, Mustang Gold, Sweet Bonanza, John Hunter and the Tomb of the
          Scarab Queen, Great Rhino MEGAWAYS, John Hunter and the Book of Tut,
          The Dog House, The Dog House MEGAWAYS, Chilli Heat, Fruit Party, Fruit
          Party 2, Wild Wild Riches, Wild West Gold, Gems Bonanza, Big Bass
          Bonanza, Big Bass Bonanza MEGAWAYS, Book of the Fallen, Madame Destiny
          MEGAWAYS, The Hand Of Midas, Juicy Fruits, Power of Thor MEGAWAYS,
          Floating Dragon Hold & Spin, Buffalo King MEGAWAYS, Rise Of Giza
          PowerNudge, Bigger Bass Bonanza, Cash Bonanza, Starlight Princess,
          Treasure Wild, Joker’s Jewels, Super X™, 5 Lions MEGAWAYS, Buffalo
          King, Magician&apos;s Secrets, Gold Party, Wild Depths, Rock Vegas,
          Gates of Valhalla, Colossal Cash Zone, The Ultimate 5, Aliens, Avalon
          II, Big Bang, Bloodsuckers, Champion of the Track, Cool Buck, Dead or
          Alive, Dr. Jekyll & Mr. Hyde, Eggomatic, High Society, Jack Hammer 2,
          Jackpot 6000, Jimi Hendrix, Kings of Chicago, Loaded, Medusa II,
          Ninja, Ozwin’s Jackpots, Playboy, Pocket Dice, Ragnarok, Scarab
          Treasure, Secret of the Stones, Simsalabim, Super Nudge 6000,
          Terminator 2, The Dark Knight, The Dark Knight Rises, The Reel Steal,
          Untamed, Book of 99, 1429 Uncharted Seas, Gold Train, Alkemor&apos;s
          Elements, Spinfinity Man, Take Santa&apos;s Shop, Take The Bank, Take
          the Kingdom, Banana Boom, Deal or no Deal, Fluffy Rangers, Forgotten
          Fable, Rocket Stars, Redrose Sanctuary, Knockout Football, Magic Oak,
          Marvelous Furlongs, Presto!, Bomb Runner, Trojan Kingdom, Agent 51,
          Blood Moon Express, Caribbean Anne 2, Caribbean Anne Gamble Feature,
          Dino Odyssey, Double Joker, Ducks till Dawn, Firedrake&apos;s Fortune,
          Goldfire7s, Pyro Pixie, Sky Hunters, Sky Hunters Gamble Feature,
          Zombie Queen, Zombie Queen Gamble Feature, Caribbean Anne, Wilds from
          the Crypt, Red Horde, Across the Universe, Wildlife Riches, Beautiful
          Bones, Serengeti Kings, Space Wars TM, Beach Invaders, Queens of Glory
          Legacy, Sic Bo, Stone Gaze, Zombies at the Door, Hood vs Wolf, Medusa
          2: the Quest of Perseus, Eye of the Kraken, Rage to Riches, Dragon
          Kingdom Eyes of Fire, Snakes and Ladders Megadice, No Comm Baccarat 1,
          Dwarfs Gone Wild, Ancients&apos; Blessing, Arcade Bomb, Fa Fa Babies,
          Get the Gold Infinireels, Rainbow Jackpots Power Lines, Totem
          Lightning Power Reels, Bulls Run Wild, 10,001 Nights MegaWay, Great
          Gold, London Tube, Monsters Unchained, Case Closed, Sirens’ Riches,
          Wanted Wildz Extreme, Wanted Wildz, Christmas Santa, Marching Legions,
          Mega Masks, Roulette Nouveau, Shark Wash, Titan Strike, Live Rush
          Roulette, Football Mania, Football Mania Deluxe, Kick Off, Larry the
          Leprechaun Xmas Edition, Lucky 9, Prosperity Pearls, Turbo Play,
          Mighty Symbols: Crowns, Black Horse™ Cash Out, 9 Lions Xmas edition,
          Firedrake&apos;s Fortune Gamble Feature, Craps, Codex of Fortune™,
          Lucky Riches Hyperspins, Mice and Magic, 10001 Nights Megaways,
          Dynamite Riches Megaways, Zillard King, Sun of Fortune, Wicked Wanda,
          Bangkok Dreams, Blazing Bull, Crystal Cavern, Atlantis Thunder,
          Mermaids Grove, Monkey King: Path of Treasure, Inca Son, Champions of
          Olympus, Myths of Bastet, The Creepy Carnival, African Spirit Sticky
          Wilds, Dragon’s Mirror, Alexander The Great World Conqueror, Reel
          Keeper Power Reels, Majestic Mysteries Power Reels, Shadow Society,
          Beriched, Dracula Awakening, Santa&apos;s Village, Egypt King, 10001
          Nights, 9 Coins Grand Diamond Edition, Holy Diver, Mad Cars, Emerald
          Bounty 7s Hold and Win, Joker Leprechauns Hold and Win, Clover Blitz
          Hold and Win, Heimdall&apos;s Gate Cash Quest, Ninjaz vs Narcos,
          Blessed Dragons Hold and Win, Diamond Bounty Xmas Hold and Win, Santa
          Blitz Hold and Win, Xmas Lightning, Allstar 7s Hold and Win, Monkey
          God Hold and Win, Hell Chef, Joker Lanterns Hold and Win, Diamond
          Supreme Hold and Win, Blazing Spirit Hold and Win, Moonrise Fortunes
          Hold and Win, Diamond Bounty 7s Hold and Win, Megaways Duel of the
          Dead BoomBoom, Big Bounty Bill BoomBoom, Deep Rush, Hyper Blitz Hold
          and Win, Flaming Scarabs, Blazing Bull Remastered, Heimdall&apos;s
          Gate, Lightning Fortune, Age of Dragons Mini-Max, Gates of Babylon
          Mini-Max, Miami Bonus Wheel Hit&apos;n&apos;Roll, Joker Leprechauns
          Hit’n’Roll, Divine Gongs, Joker Max: Hit&apos;n&apos;roll Xmas
          Edition, Phat Cats Megaways, Crystal Cavern Mini Max, Blazing Bull 2
          Mini Max, Joker Fortune Blitz, FruitMax: Cashlinez, Pearls of
          Aphrodite MiniMax, Caribbean Anne MiniMax, Joker Lanterns: Hit
          &apos;n&apos; Roll, Rumble Ratz Megaways, Safari Chase: Hit
          &apos;n&apos; Roll, Blazing Bull: Cash Quest, Goblins & Gemstones: Hit
          &apos;n&apos; Roll, Joker Max: Hit &apos;n&apos; Roll, Super Size
          Atlas, Legend of Senteng, Holmes: Reel Detective, Joker Times Xmas,
          Griffin&apos;s Quest, Blazing Bull Gamble Feature, Jewels of Jupiter,
          Pearls of Aphrodite, Blazing Bull 2, Cosmic Charms, Age of Huracan,
          Operation Diamond Hunt Gamble Feature, Bangkok Dreams Gamble Feature,
          Finnegan&apos;s Formula, Joker Times, Speakeasy Boost, Goblins &
          Gemstones, Griffin’s Quest Xmas Edition, Sadie Swift Guns&apos;n
          Glyphs, Pawprints of Purrsia, Beers on Reels, Maui Millions,
          Griffin&apos;s Quest Gamble Feature, Miami Bonus Wheel, Temple of
          Heroes, Big Bounty Bill, Operation Diamond Hunt, Wildcraft, Midas
          Treasure, Doctor Electro, Age of Ice Dragons, Hong Bao, Desert Gem,
          Wildmine, Age of Dragons, Monkey God, Gates of Babylon, Tree of Gold,
          Fire Eagle, Lucky Fridays, The WishMaster Megaways, Multiplier Riches,
          Kochbuch, The Evil Bet
        </Typography>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Stack gap={2}>
      <Stack
        gap={1}
        paddingInlineEnd={[0, 0, '40%']}
        borderRadius={1}
        overflow={'hidden'}
        position={'relative'}
        sx={{
          backgroundImage: `url(${bannerBg.src})`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '105%',
          ['&::before']: auroraProps,
          [theme.breakpoints.down('md')]: {
            backgroundImage: 'none',
            ['&::before']: {
              display: 'none',
            },
          },
        }}
      >
        <h1>{t('benefits::Blast Off with a $8,000 Welcome Package!')}</h1>
        <Typography color={'text.secondary'}>
          <Trans
            i18nKey="benefits_deposit_1"
            ns="benefits"
            components={{
              br: (
                <Divider
                  sx={{
                    border: 'none',
                    mt: 1,
                    mb: 1,
                  }}
                />
              ),
              b: (
                <b
                  style={{
                    display: 'inline',
                  }}
                />
              ),
            }}
            defaults={
              'At MoonClub, new players can grab a bonus on their first four deposits. Our generous welcome package offers a <b>650% bonus up to $8,000.</b> <br/> This means you can get started with more bang for your buck and enjoy more play on the best casino games.'
            }
          ></Trans>
        </Typography>
        <Box
          display={[undefined, undefined, 'none']}
          sx={{
            ['&::before']: {
              ...auroraProps,
              width: '20vw',
              height: '20vw',
              left: '15vw',
              bottom: '15vw',
            },
          }}
        >
          <Image
            src={bannerBg}
            alt="banner-mobile"
            style={{
              aspectRatio: '504/252',
              width: '100%',
              height: 'auto',
            }}
          />
        </Box>
      </Stack>
      <Stack display={[undefined, undefined, 'none']}>
        <Image
          src={depositAll}
          alt="depositAll"
          style={{
            aspectRatio: '1',
            width: '100%',
            height: 'auto',
          }}
        />
      </Stack>
      <Stack
        display={['none', 'none', 'flex']}
        mt={8}
        direction={'row'}
        maxWidth={835}
        gap={2}
        mx={'auto'}
        sx={{
          ['img.stage-img']: {
            aspectRatio: '195/234',
            height: 'auto',
            transition: 'transform 0.3s',
            position: 'relative',
            ['&:hover']: {
              cursor: 'pointer',
              transform: 'translateY(-20px)',
            },
          },
          ['img.arrow']: {
            width: '40px',
            height: '40px',
            objectFit: 'contain',
            position: 'absolute',
            top: '50%',
            right: '0',
            transform: 'translateY(-50%) translateX(75%)',
            backdropFilter: 'blur(10px)',
            borderRadius: '50%',
          },
        }}
      >
        {[stage1, stage2, stage3, stage4].map((stage, index) => (
          <Stack key={index} position={'relative'}>
            <Image
              src={stage}
              alt={`stage${index + 1}`}
              style={{
                transform:
                  index === completedCount ? 'translateY(-20px)' : undefined,
                zIndex: 8 - index * 2,
              }}
              className="stage-img"
            />
            {index !== 3 && (
              <img
                src={arrow.src}
                alt="arrow"
                className="arrow"
                style={{
                  zIndex: 7 - index * 2,
                }}
              />
            )}

            {depositBtn(index)}
          </Stack>
        ))}
      </Stack>
      <Typography variant="h4" margin={'24px auto'}>
        {t('benefits::benefits_deposit_2', {
          defaultValue: 'Enjoy More from your First Welcome Bonus',
        })}
      </Typography>
      <Stack
        borderRadius={1}
        margin={'auto'}
        padding={1}
        maxWidth={326}
        alignItems={'center'}
        gap={1}
      >
        <Image
          src={stage10min}
          alt={`stage1`}
          className="stage-img"
          width={195}
          height={237}
        />
        <Typography>
          {t('benefits::benefits_deposit_3', {
            defaultValue:
              'Deposit within 60 minutes of signing up and get a bigger welcome bonus!',
          })}
        </Typography>
        <Box display={[undefined, undefined, 'none']}>
          {depositBtn(completedCount)}
        </Box>
      </Stack>
      <Stack
        borderRadius={1}
        margin={'auto'}
        padding={1}
        maxWidth={326}
        alignItems={'center'}
        gap={1}
        display={['none']}
      >
        <Image
          src={stageTg}
          alt={`stage-tg`}
          className="stage-tg"
          width={195}
          height={237}
        />
        <Typography>
          {t('benefits::benefits_deposit_tg', {
            defaultValue:
              'Make your first deposit via Telegram and get a bigger welcome bonus!',
          })}
        </Typography>
      </Stack>
      <Stack mt={4} gap={2}>
        <Typography variant="h4">
          {t('benefits::benefits_deposit_4', {
            defaultValue: 'Get Early Access to your Bonus Winnings',
          })}
        </Typography>
        <Typography color={'text.secondary'}>
          {t('benefits::benefits_deposit_5', {
            defaultValue:
              'We know wagering requirements can be challenging to hit. That’s why we credit your bonus funds in 10 increments. Here’s how it works, when wagered through your deposit amount six times, you will release 10% of the total bonus.',
          })}
        </Typography>
        <Typography fontWeight={700} color={'text.secondary'}>
          {t('benefits::benefits_deposit_6', {
            defaultValue: 'Here’s an example:',
          })}
        </Typography>
        <Typography color={'text.secondary'}>
          {t('benefits::benefits_deposit_7', {
            defaultValue:
              'If you deposit $100, you will get $125 in bonus money. When you wager a total of $600 ($100 x 6), you release $12.50 of the bonus (10% of the $125 bonus).',
          })}
        </Typography>
      </Stack>
      {tcBlock()}
      {gamesBlock}
      {tcBlock('reload')}
      {gamesBlock}
    </Stack>
  );
};

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await getI18nProps({ locale, additionalNamespaces: ['about'] })),
    },
  };
}

export default DepositBenefits;
